import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Button } from "./ui/button";
import { useRouter } from "next/navigation";
import React, { useState, useEffect } from "react";
import { ProtocolList } from "@/components/protocols/ProtocolList";
import axios from "axios";
// @ts-ignore - Prisma client types
import type {  Protocol  } from '@prisma/client';
import { CreateEventModal } from "@/components/modals/create-event-modal";
import { CreateChallengeModal } from "@/components/modals/create-challenge-modal";
import { CreateProtocolModal } from "@/components/modals/create-protocol-modal";
import {
  LayoutGrid,
  Users,
  LineChart,
  ClipboardList,
  Apple,
  Dumbbell,
  Activity,
  Dna,
  Watch,
  FlaskConical,
  Target,
  Medal,
  TrendingUp,
  Mountain,
  Milestone,
  AlertTriangle,
  Stethoscope,
  Ticket
} from "lucide-react";

interface ProfileContentPickerProps {
  defaultValue?: string;
  onValueChange: (value: string, subValue?: string, timeFilter?: string) => void;
}

const ProfileContentPicker: React.FC<ProfileContentPickerProps> = ({
  defaultValue = "all",
  onValueChange
}) => {
  const router = useRouter();
  const [activeSubTab, setActiveSubTab] = useState<string>("");
  const [timeFilter, setTimeFilter] = useState<string>("24h");
  const [isCreateEventOpen, setIsCreateEventOpen] = useState(false);
  const [isCreateChallengeOpen, setIsCreateChallengeOpen] = useState(false);
  const [isCreateProtocolOpen, setIsCreateProtocolOpen] = useState(false);
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [isLoadingProtocols, setIsLoadingProtocols] = useState(false);

  const fetchProtocols = async () => {
    try {
      setIsLoadingProtocols(true);
      const response = await axios.get('/api/protocols');
      setProtocols(response.data);
    } catch (error) {
      console.error('Failed to fetch protocols:', error);
    } finally {
      setIsLoadingProtocols(false);
    }
  };

  useEffect(() => {
    if (defaultValue === 'protocols') {
      fetchProtocols();
    }
  }, [defaultValue]);

  const handleMainTabChange = (value: string) => {
    let defaultSubTab = "";
    switch (value) {
      case "social":
        defaultSubTab = "posts";
        break;
      case "recaps":
        defaultSubTab = "daily";
        break;
      case "protocols":
        defaultSubTab = "medication";
        fetchProtocols();
        break;
      case "nutrition":
        defaultSubTab = "meals";
        break;
      case "fitness":
        defaultSubTab = "workouts";
        break;
      case "biomarkers":
        defaultSubTab = "wearables";
        break;
      case "genome":
        defaultSubTab = "disease";
        break;
      case "journey":
      case "milestones":
      case "limitations":
      case "therapies":
        defaultSubTab = "";
        break;
      case "challenges":
        defaultSubTab = "overview";
        break;
      case "events":
        defaultSubTab = "upcoming";
        break;
      case "awards":
      case "progress":
        defaultSubTab = "";
        break;
      default:
        defaultSubTab = "";
    }
    setActiveSubTab(defaultSubTab);
    setTimeFilter("24h");
    onValueChange(value, defaultSubTab, "24h");
  };

  const handleSubTabChange = (value: string) => {
    setActiveSubTab(value);
    onValueChange(defaultValue, value, timeFilter);
  };

  const handleTimeFilterChange = (value: string) => {
    setTimeFilter(value);
    onValueChange(defaultValue, activeSubTab, value);
  };

  const TimeFilters = () => (
    <TabsList>
      <TabsTrigger value="24h">Past 24h</TabsTrigger>
      <TabsTrigger value="7d">7d</TabsTrigger>
      <TabsTrigger value="30d">30d</TabsTrigger>
      <TabsTrigger value="1y">1y</TabsTrigger>
    </TabsList>
  );

  return (
    <div className="space-y-4">
      <CreateEventModal
        isOpen={isCreateEventOpen}
        onClose={() => setIsCreateEventOpen(false)}
      />
      <CreateChallengeModal
        isOpen={isCreateChallengeOpen}
        onClose={() => setIsCreateChallengeOpen(false)}
      />
      <CreateProtocolModal
        isOpen={isCreateProtocolOpen}
        onClose={() => setIsCreateProtocolOpen(false)}
        onSuccess={fetchProtocols}
      />
      <Tabs defaultValue={defaultValue} onValueChange={handleMainTabChange} className="w-full">
        <div className="bg-card p-4 rounded-lg">
          <TabsList className="w-full grid grid-cols-4 gap-2 mb-2">
            <TabsTrigger value="all" className="flex items-center gap-2">
              <LayoutGrid className="h-4 w-4" />
              All
            </TabsTrigger>
            <TabsTrigger value="social" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Social
            </TabsTrigger>
            <TabsTrigger value="recaps" className="flex items-center gap-2">
              <LineChart className="h-4 w-4" />
              Recaps
            </TabsTrigger>
            <TabsTrigger value="protocols" className="flex items-center gap-2">
              <ClipboardList className="h-4 w-4" />
              Protocols
            </TabsTrigger>
          </TabsList>
          <TabsList className="w-full grid grid-cols-4 gap-2 mb-2">
            <TabsTrigger value="biomarkers" className="flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Biomarkers
            </TabsTrigger>
            <TabsTrigger value="nutrition" className="flex items-center gap-2">
              <Apple className="h-4 w-4" />
              Nutrition
            </TabsTrigger>
            <TabsTrigger value="fitness" className="flex items-center gap-2">
              <Dumbbell className="h-4 w-4" />
              Fitness
            </TabsTrigger>
            <TabsTrigger value="genome" className="flex items-center gap-2">
              <Dna className="h-4 w-4" />
              Genome
            </TabsTrigger>
          </TabsList>
          <TabsList className="w-full grid grid-cols-4 gap-2 mb-2">
            <TabsTrigger value="journey" className="flex items-center gap-2">
              <Mountain className="h-4 w-4" />
              Journey
            </TabsTrigger>
            <TabsTrigger value="milestones" className="flex items-center gap-2">
              <Milestone className="h-4 w-4" />
              Milestones
            </TabsTrigger>
            <TabsTrigger value="limitations" className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Limitations
            </TabsTrigger>
            <TabsTrigger value="therapies" className="flex items-center gap-2">
              <Stethoscope className="h-4 w-4" />
              Therapies
            </TabsTrigger>
          </TabsList>
          <TabsList className="w-full grid grid-cols-4 gap-2">
            <TabsTrigger value="challenges" className="flex items-center gap-2">
              <Target className="h-4 w-4" />
              Challenges
            </TabsTrigger>
            <TabsTrigger value="events" className="flex items-center gap-2">
              <Ticket className="h-4 w-4" />
              Events
            </TabsTrigger>
            <TabsTrigger value="awards" className="flex items-center gap-2">
              <Medal className="h-4 w-4" />
              Awards
            </TabsTrigger>
            <TabsTrigger value="progress" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Progress
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="all" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">All Activity</h2>
            <Tabs defaultValue="24h" onValueChange={handleTimeFilterChange}>
              <TimeFilters />
            </Tabs>
          </div>
        </TabsContent>

        <TabsContent value="social" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Social Activity</h2>
            <Tabs defaultValue="24h" onValueChange={handleTimeFilterChange}>
              <TimeFilters />
            </Tabs>
          </div>
          <Tabs defaultValue="posts" onValueChange={handleSubTabChange}>
            <TabsList className="w-full">
              <TabsTrigger value="posts">Posts</TabsTrigger>
              <TabsTrigger value="replies">Replies</TabsTrigger>
              <TabsTrigger value="highlights">Highlights</TabsTrigger>
              <TabsTrigger value="articles">Articles</TabsTrigger>
              <TabsTrigger value="media">Media</TabsTrigger>
              <TabsTrigger value="likes">Likes</TabsTrigger>
            </TabsList>
          </Tabs>
        </TabsContent>

        <TabsContent value="recaps" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Health & Activity Recaps</h2>
            <Tabs defaultValue="24h" onValueChange={handleTimeFilterChange}>
              <TimeFilters />
            </Tabs>
          </div>
          <Tabs defaultValue="daily" onValueChange={handleSubTabChange}>
            <TabsList className="w-full">
              <TabsTrigger value="daily">Daily</TabsTrigger>
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
              <TabsTrigger value="yearly">Yearly</TabsTrigger>
            </TabsList>
          </Tabs>
        </TabsContent>

        <TabsContent value="protocols" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold">Health Protocols</h2>
              <Button
                onClick={() => setIsCreateProtocolOpen(true)}
                size="sm"
                variant="outline"
              >
                Create Protocol
              </Button>
            </div>
          </div>
          {isLoadingProtocols ? (
            <div className="text-center text-muted-foreground mt-4">
              Loading protocols...
            </div>
          ) : (
            <ProtocolList protocols={protocols} />
          )}
        </TabsContent>

        <TabsContent value="nutrition" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Nutrition Tracking</h2>
            <Tabs defaultValue="24h" onValueChange={handleTimeFilterChange}>
              <TimeFilters />
            </Tabs>
          </div>
          <Tabs defaultValue="meals" onValueChange={handleSubTabChange}>
            <TabsList className="w-full">
              <TabsTrigger value="meals">Meals</TabsTrigger>
              <TabsTrigger value="macros">Macros</TabsTrigger>
              <TabsTrigger value="water">Water Intake</TabsTrigger>
            </TabsList>
          </Tabs>
        </TabsContent>

        <TabsContent value="fitness" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Fitness Activity</h2>
            <Tabs defaultValue="24h" onValueChange={handleTimeFilterChange}>
              <TimeFilters />
            </Tabs>
          </div>
          <Tabs defaultValue="workouts" onValueChange={handleSubTabChange}>
            <TabsList className="w-full">
              <TabsTrigger value="workouts">Workouts</TabsTrigger>
              <TabsTrigger value="steps">Steps/Walks</TabsTrigger>
              <TabsTrigger value="yoga">Yoga/Stretching</TabsTrigger>
            </TabsList>
          </Tabs>
        </TabsContent>

        <TabsContent value="biomarkers" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Biomarkers</h2>
            <Tabs defaultValue="24h" onValueChange={handleTimeFilterChange}>
              <TimeFilters />
            </Tabs>
          </div>
          <Tabs defaultValue="wearables" onValueChange={handleSubTabChange}>
            <TabsList className="w-full">
              <TabsTrigger value="wearables" className="flex items-center gap-2">
                <Watch className="h-4 w-4" />
                Wearables
              </TabsTrigger>
              <TabsTrigger value="labs" className="flex items-center gap-2">
                <FlaskConical className="h-4 w-4" />
                Labs
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </TabsContent>

        <TabsContent value="genome" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Genomic Analysis</h2>
          </div>
          <Tabs defaultValue="disease" onValueChange={handleSubTabChange}>
            <TabsList className="w-full grid grid-cols-2 lg:grid-cols-4 gap-1">
              <TabsTrigger value="disease">Disease Risk</TabsTrigger>
              <TabsTrigger value="pharma">Pharmacogenomics</TabsTrigger>
              <TabsTrigger value="nutri">Nutrigenomics</TabsTrigger>
              <TabsTrigger value="fitness">Fitness Genomics</TabsTrigger>
              <TabsTrigger value="carrier">Carrier Screening</TabsTrigger>
              <TabsTrigger value="behavioral">Behavioral Genetics</TabsTrigger>
              <TabsTrigger value="ancestry">Ancestry</TabsTrigger>
            </TabsList>
          </Tabs>
        </TabsContent>

        <TabsContent value="journey" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Health Journey</h2>
            <Tabs defaultValue="24h" onValueChange={handleTimeFilterChange}>
              <TimeFilters />
            </Tabs>
          </div>
          <div className="space-y-6">
            <p>Track your health and wellness journey over time.</p>
          </div>
        </TabsContent>

        <TabsContent value="milestones" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Journey Milestones</h2>
            <Tabs defaultValue="24h" onValueChange={handleTimeFilterChange}>
              <TimeFilters />
            </Tabs>
          </div>
          <div className="space-y-6">
            <p>Key achievements and milestones in your health journey.</p>
          </div>
        </TabsContent>

        <TabsContent value="limitations" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Current Limitations</h2>
            <Tabs defaultValue="24h" onValueChange={handleTimeFilterChange}>
              <TimeFilters />
            </Tabs>
          </div>
          <div className="space-y-6">
            <p>Track and manage your health limitations and restrictions.</p>
          </div>
        </TabsContent>

        <TabsContent value="therapies" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Active Therapies</h2>
            <Tabs defaultValue="24h" onValueChange={handleTimeFilterChange}>
              <TimeFilters />
            </Tabs>
          </div>
          <div className="space-y-6">
            <p>Monitor your ongoing therapeutic treatments and progress.</p>
          </div>
        </TabsContent>

        <TabsContent value="challenges" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold">Challenges</h2>
              <Button
                onClick={() => setIsCreateChallengeOpen(true)}
                size="sm"
                variant="outline"
              >
                Create Challenge
              </Button>
              <Button
                onClick={() => router.push('/challenges')}
                size="sm"
                variant="outline"
              >
                Explore Challenges
              </Button>
            </div>
            <Tabs defaultValue="24h" onValueChange={handleTimeFilterChange}>
              <TimeFilters />
            </Tabs>
          </div>
          <Tabs defaultValue="overview" onValueChange={handleSubTabChange}>
            <TabsList className="w-full">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="created">Created</TabsTrigger>
              <TabsTrigger value="joined">Joined</TabsTrigger>
              <TabsTrigger value="completed">Completed</TabsTrigger>
              <TabsTrigger value="failed">Failed</TabsTrigger>
            </TabsList>
          </Tabs>
        </TabsContent>

        <TabsContent value="awards" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Your Awards</h2>
          </div>
        </TabsContent>

        <TabsContent value="progress" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Overall Progress</h2>
            <Tabs defaultValue="24h" onValueChange={handleTimeFilterChange}>
              <TimeFilters />
            </Tabs>
          </div>
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-4">Health Score Trends</h3>
              <p>Your health score trends and improvements will be displayed here.</p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">Achievement Timeline</h3>
              <p>Timeline of your health and wellness achievements.</p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="events" className="mt-4">
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold">Events</h2>
              <Button
                onClick={() => setIsCreateEventOpen(true)}
                size="sm"
                variant="outline"
              >
                Create Event
              </Button>
              <Button
                onClick={() => router.push('/events')}
                size="sm"
                variant="outline"
              >
                Explore Events
              </Button>
            </div>
            <Tabs defaultValue="24h" onValueChange={handleTimeFilterChange}>
              <TimeFilters />
            </Tabs>
          </div>
          <Tabs defaultValue="upcoming" onValueChange={handleSubTabChange}>
            <TabsList className="w-full">
              <TabsTrigger value="upcoming">Upcoming</TabsTrigger>
              <TabsTrigger value="past">Past</TabsTrigger>
              <TabsTrigger value="created">Created</TabsTrigger>
              <TabsTrigger value="saved">Saved</TabsTrigger>
            </TabsList>
          </Tabs>
          <div className="space-y-6 mt-4">
            {activeSubTab === "upcoming" && (
              <div>
                <p>Your scheduled health and wellness events will be displayed here.</p>
              </div>
            )}
            {activeSubTab === "past" && (
              <div>
                <p>History of events you've attended or organized.</p>
              </div>
            )}
            {activeSubTab === "created" && (
              <div>
                <p>Events you've organized or created.</p>
              </div>
            )}
            {activeSubTab === "saved" && (
              <div>
                <p>Events you've bookmarked for later.</p>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProfileContentPicker;